import React, { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { employeeAttributesValidationSchema } from "../../utils/ValidationSchema";
import { EmployeeAttributesFormType } from "../../utils/formType";
import { EmployeeAttributesInitialFormValues } from "../../utils/initialFormValues";
import EmployeeAttributesComponent from "./component/EmployeeAttributesComponent";
import { EmployeeAttributeType } from "types";
import {
  useUpdateEmployeeAttributesMutation,
  useUpdateEmployeeDeliveryAttributeMutation,
} from "types/generated/operations";
import { getProspectUuid } from "utils/idHelpers";
import {
  convertStringIntoBool,
  ConvertToISODate,
  errorMessageMap,
} from "../../../../utils/helper";
import FooterButton from "uicomponents/FooterButton";
import Alert from "uicomponents/Alert";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
} from "pages/WorkersCompApplcation/utils/contants";
import {
  trackFormError,
  trackPageViewUserStep,
} from "utils/analytics/trackEvent";

interface IEmployeeAttributesProps {
  setNextStep: () => void;
  setPreviousStep: () => void;
  data: EmployeeAttributeType;
}

const EmployeeAttributes = ({
  ...props
}: IEmployeeAttributesProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(5, "employee attributes");
  }, []);
  const { setNextStep, setPreviousStep, data } = props;

  const initialFormValues: EmployeeAttributesFormType =
    EmployeeAttributesInitialFormValues(data);

  const [
    updateEmployeeAttributes,
    { loading: updateEmployeeAttributesLoading },
  ] = useUpdateEmployeeAttributesMutation();
  const [updateEmployeeDelivery, { loading: updateEmployeeDeliveryLoading }] =
    useUpdateEmployeeDeliveryAttributeMutation();

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const handleOnSubmit = async (values: EmployeeAttributesFormType) => {
    try {
      setIsLoadingSteps(true);
      await updateEmployeeDelivery({
        variables: {
          uuid: getProspectUuid(),
          deliveries: convertStringIntoBool(values.deliveries),
          employeesWhoMakeDeliveries: values.employeesWhoMakeDeliveries
            ?.filter((emp) => !!emp.employeeId)
            .map((eachEmp) => {
              return {
                ...eachEmp,
                deliveryTransportationType:
                  eachEmp.deliveryTransportationType || null,
                deliveryDistance: eachEmp.deliveryDistance || null,
                deliveryFrequency: eachEmp.deliveryFrequency,
              };
            }),
        },
      });
      await updateEmployeeAttributes({
        variables: {
          uuid: getProspectUuid(),
          boatsBridges: convertStringIntoBool(values.boatsBridges),
          hardware: convertStringIntoBool(values.hardware),
          employeesPhysicalLabor: convertStringIntoBool(
            values.employeesPhysicalLabor
          ),
          employeesWhoTravel: values.employeesWhoTravel
            ?.filter((emp) => !!emp.employeeId)
            .map((eachEmp) => {
              return {
                ...eachEmp,
                travelInternationally: convertStringIntoBool(
                  eachEmp.travelInternationally
                ),
                travelLocation: convertStringIntoBool(
                  eachEmp.travelInternationally
                )
                  ? eachEmp.travelLocation
                  : "US only",
              };
            }),
          underageOrSeniorEmployees: values.underageOrSeniorEmployees
            ?.filter((emp) => !!emp.employeeId)
            .map((eachEmp) => {
              return {
                ...eachEmp,
                dateOfBirth: ConvertToISODate(eachEmp.dateOfBirth) || null,
              };
            }),
          employeesWhoUseTools: values.employeesWhoUseTools?.filter(
            (emp) => !!emp.employeeId
          ),
          employeesWhoRequirePhysicalLabor:
            values.employeesWhoRequirePhysicalLabor
              ?.filter((emp) => !!emp.employeeId)
              .map((eachEmp) => {
                return {
                  ...eachEmp,
                  percentPhysicalLabor: eachEmp.percentPhysicalLabor || null,
                };
              }),
          employeesWhoUseBoatsBridges:
            values.employeesWhoUseBoatsBridges?.filter((id) => !!id),
          employeesWhoHavePhysicalDisabilities:
            values.employeesWhoHavePhysicalDisabilities?.filter((id) => !!id),
          employeesWhoWorkUnderground: values.employeesWhoWorkUnderground
            ?.filter((emp) => !!emp.employeeId)
            .map((eachEmp) => {
              return {
                ...eachEmp,
                undergroundDescription: eachEmp.undergroundDescription || null,
              };
            }),
          employeesWhoWorkOnLadders: values.employeesWhoWorkOnLadders
            ?.filter((emp) => !!emp.employeeId)
            .map((eachEmp) => {
              return {
                ...eachEmp,
                laddersDescription: eachEmp.laddersDescription || null,
              };
            }),
          employeesWhoPerformHeavyLifting:
            values.employeesWhoPerformHeavyLifting
              ?.filter((emp) => !!emp.employeeId)
              .map((eachEmp) => {
                return {
                  ...eachEmp,
                  maximumWeightLifted: eachEmp.maximumWeightLifted || null,
                };
              }),
          employmentDuration: values.employmentDuration,
          physicalDisability: convertStringIntoBool(values.physicalDisability),
          employeesWorkUnderground: convertStringIntoBool(
            values.employeesWorkUnderground
          ),
          employeesWorkOnLadders: convertStringIntoBool(
            values.employeesWorkOnLadders
          ),
          numberTemp: values.numberTemp,
          contractorWorkType: values.contractorWorkType,
          contractorsHaveWorkComp: convertStringIntoBool(
            values.contractorsHaveWorkComp
          ),
          contractorsHaveWorkersCompCOIS: convertStringIntoBool(
            values.contractorsHaveWorkersCompCois
          ),
          numberContractors: values.numberContractors,
          percentContractors: values.percentContractors,
          hasContractors: convertStringIntoBool(values.hasContractors),
          travel: convertStringIntoBool(values.travel),
          underageOrSenior: convertStringIntoBool(values.underageOrSenior),
          employeePerformHeavyLifting: convertStringIntoBool(
            values.employeePerformHeavyLifting
          ),
        },
      });
      setNextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleBack = (): void => {
    setPreviousStep();
  };

  const fieldRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Helper function that makes the browser scroll a specific field into view
  const focusField = (fieldName: string) => {
    if (fieldRefs.current[fieldName]) {
      fieldRefs.current[fieldName]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <>
      <Formik
        initialValues={initialFormValues}
        validationSchema={employeeAttributesValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ errors, handleSubmit, values }) => {
          return (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                const firstErrorFieldName = Object.keys(errors)[0];
                focusField(firstErrorFieldName);
                handleSubmit(e);
              }}
            >
              <EmployeeAttributesComponent
                formvalues={values}
                hasEmployeesData={initialFormValues}
                employeeCensus={data?.employeeCensuses}
                fieldRefs={fieldRefs}
              />
              <FooterButton
                nextButtonText={
                  updateEmployeeAttributesLoading ||
                  updateEmployeeDeliveryLoading ||
                  isLoadingSteps
                    ? LOADING_BUTTON_TEXT
                    : NEXT_BUTTON_TEXT
                }
                backButtonText={BACK_BUTTON_TEXT}
                disabled={
                  updateEmployeeAttributesLoading ||
                  updateEmployeeDeliveryLoading ||
                  isLoadingSteps
                }
                handleBackClick={handleBack}
              />
              {isError && <Alert message={errorMessage} />}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default EmployeeAttributes;
